import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Clock,
  MoreHorizontal,
  Search,
  ShieldAlert,
  ShieldOff,
  ShieldPlus,
  Tag,
  UserX,
  X,
} from 'lucide-react';
import { ActionMenu } from '@/components/ActionMenu';
import { useGuildRights } from '@/lib/access';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * Member-Liste + inline Moderations-Aktionen.
 *
 * Fetches /api/guilds/:id/members (Bot-token proxy, 60s cache) plus the guild
 * roles for label resolution. Actions post to the existing /api/actions/*
 * endpoints (ban/kick/timeout/assign-role) — the ActionRow expands inline into
 * a form for whichever action was picked so we don't need a modal system.
 */

interface Member {
  id: string;
  username: string;
  rawUsername: string;
  nick: string | null;
  displayName: string;
  avatarUrl: string | null;
  bot: boolean;
  roles: string[];
  joinedAt: string;
  premiumSince: string | null;
  timeoutUntil: string | null;
}

interface Role {
  id: string;
  name: string;
  color: number;
  position: number;
}

type Filter = 'all' | 'humans' | 'bots' | 'timeout';

export function Members() {
  const { guildId } = useParams<{ guildId: string }>();
  const membersQuery = useQuery({
    queryKey: ['members', guildId],
    queryFn: () =>
      api<{ members: Member[]; fetchedAt: string }>(
        `/api/guilds/${guildId}/members`
      ),
    enabled: !!guildId,
    staleTime: 60_000,
  });
  const rolesQuery = useQuery({
    queryKey: ['guild-roles', guildId],
    queryFn: () => api<{ roles: Role[] }>(`/api/guilds/${guildId}/roles`),
    enabled: !!guildId,
    staleTime: 60_000,
  });

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('humans');

  const roleMap = useMemo(() => {
    const m = new Map<string, Role>();
    for (const r of rolesQuery.data?.roles ?? []) m.set(r.id, r);
    return m;
  }, [rolesQuery.data]);

  const filtered = useMemo(() => {
    const members = membersQuery.data?.members ?? [];
    const q = query.trim().toLowerCase();
    const now = Date.now();
    return members
      .filter((m) => {
        if (filter === 'humans' && m.bot) return false;
        if (filter === 'bots' && !m.bot) return false;
        if (filter === 'timeout') {
          if (!m.timeoutUntil) return false;
          if (new Date(m.timeoutUntil).getTime() <= now) return false;
        }
        if (!q) return true;
        return (
          m.username.toLowerCase().includes(q) ||
          m.rawUsername.toLowerCase().includes(q) ||
          (m.nick?.toLowerCase().includes(q) ?? false) ||
          m.id.includes(q)
        );
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [membersQuery.data, filter, query]);

  const humanCount = (membersQuery.data?.members ?? []).filter((m) => !m.bot).length;
  const botCount = (membersQuery.data?.members ?? []).length - humanCount;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Mitglieder</h1>
        {membersQuery.data?.fetchedAt && (
          <span className="text-xs text-muted-foreground">
            {membersQuery.data.members.length} Mitglieder · geladen{' '}
            {new Date(membersQuery.data.fetchedAt).toLocaleTimeString('de-DE')}
          </span>
        )}
      </div>

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="min-w-[240px] flex-1">
            <Label>Suche</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, Nickname oder ID"
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <Label>Filter</Label>
            <div className="flex gap-1">
              {(
                [
                  ['humans', `Menschen (${humanCount})`],
                  ['bots', `Bots (${botCount})`],
                  ['timeout', 'Nur Timeouts'],
                  ['all', 'Alle'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-xs',
                    filter === key
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'bg-background hover:bg-accent'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {membersQuery.isLoading && (
        <p className="text-muted-foreground">Lade Mitglieder…</p>
      )}
      {membersQuery.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              {(membersQuery.error as Error).message}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Fehler 403? Der Bot braucht das <strong>Server Members Intent</strong>{' '}
              (privileged) im Discord Developer Portal.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {filtered.map((m) => (
          <MemberRow
            key={m.id}
            guildId={guildId!}
            member={m}
            roleMap={roleMap}
            allRoles={rolesQuery.data?.roles ?? []}
          />
        ))}
        {!membersQuery.isLoading && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Keine Mitglieder passen zum Filter.
          </p>
        )}
      </div>
    </div>
  );
}

// --------------------------- Row ---------------------------

type ActionKind = 'ban' | 'kick' | 'timeout' | 'warn' | 'role' | 'setnick' | 'permaban' | null;

function MemberRow({
  guildId,
  member,
  roleMap,
  allRoles,
}: {
  guildId: string;
  member: Member;
  roleMap: Map<string, Role>;
  allRoles: Role[];
}) {
  const [action, setAction] = useState<ActionKind>(null);
  // Actions belong to their plugin (lib/access.ts), not to this page.
  const { can } = useGuildRights(guildId);
  const canModerate = can('moderation', 'edit');
  const canPermaban = can('permabans', 'edit');
  const memberRoles = member.roles
    .map((id) => roleMap.get(id))
    .filter((r): r is Role => !!r)
    .sort((a, b) => b.position - a.position);
  const now = Date.now();
  const timedOut =
    member.timeoutUntil && new Date(member.timeoutUntil).getTime() > now;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          {member.avatarUrl ? (
            <img
              src={member.avatarUrl}
              alt=""
              className="h-10 w-10 rounded-full"
              loading="lazy"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-muted" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">{member.displayName}</CardTitle>
              {member.bot && (
                <span className="rounded-md bg-primary/20 px-1.5 py-0.5 text-[10px] text-primary">
                  BOT
                </span>
              )}
              {timedOut && (
                <span className="inline-flex items-center gap-1 rounded-md bg-destructive/20 px-1.5 py-0.5 text-[10px] text-destructive">
                  <Clock className="h-3 w-3" /> Timeout bis{' '}
                  {new Date(member.timeoutUntil!).toLocaleString('de-DE')}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              @{member.rawUsername} · <code className="text-[10px]">{member.id}</code>
            </div>
            {memberRoles.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {memberRoles.map((r) => (
                  <span
                    key={r.id}
                    className="rounded-full border px-2 py-0.5 text-[10px]"
                    style={
                      r.color
                        ? {
                            color: `#${r.color.toString(16).padStart(6, '0')}`,
                            borderColor: `#${r.color.toString(16).padStart(6, '0')}55`,
                          }
                        : undefined
                    }
                  >
                    {r.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {(canModerate || canPermaban) && (
          <ActionMenu
            trigger={
              <Button variant="ghost" size="sm" className="h-8" title="Aktionen">
                <MoreHorizontal className="h-4 w-4" />
                <span className="ml-1 hidden text-xs md:inline">Aktionen</span>
              </Button>
            }
            items={[
              ...(canModerate ? [
              { key: 'warn', label: 'Verwarnen', icon: <ShieldAlert className="h-4 w-4" />, onSelect: () => setAction('warn') },
              { key: 'setnick', label: 'Nickname', icon: <Tag className="h-4 w-4" />, onSelect: () => setAction('setnick') },
              { key: 'timeout', label: 'Timeout', icon: <Clock className="h-4 w-4" />, onSelect: () => setAction('timeout') },
              { key: 'role', label: 'Rolle geben/entfernen', icon: <ShieldPlus className="h-4 w-4" />, onSelect: () => setAction('role') },
              { key: 'kick', label: 'Kicken', icon: <UserX className="h-4 w-4" />, onSelect: () => setAction('kick'), destructive: true },
              { key: 'ban', label: 'Bannen', icon: <Ban className="h-4 w-4" />, onSelect: () => setAction('ban'), destructive: true },
              ] : []),
              ...(canPermaban
                ? [{ key: 'permaban', label: 'Permaban (überlebt Unbans)', icon: <ShieldOff className="h-4 w-4" />, onSelect: () => setAction('permaban'), destructive: true }]
                : []),
            ]}
          />
          )}
        </div>
      </CardHeader>
      {action && (
        <CardContent>
          <ActionForm
            key={action}
            guildId={guildId}
            member={member}
            action={action}
            allRoles={allRoles}
            memberRoles={memberRoles}
            onClose={() => setAction(null)}
          />
        </CardContent>
      )}
    </Card>
  );
}

// --------------------------- Action form ---------------------------

function ActionForm({
  guildId,
  member,
  action,
  allRoles,
  memberRoles,
  onClose,
}: {
  guildId: string;
  member: Member;
  action: Exclude<ActionKind, null>;
  allRoles: Role[];
  memberRoles: Role[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(600); // seconds
  const [roleId, setRoleId] = useState('');
  const [op, setOp] = useState<'add' | 'remove'>('add');
  const [nickname, setNickname] = useState(member.nick ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (action === 'setnick') {
        return api('/api/actions/member-setnick', {
          method: 'POST',
          body: JSON.stringify({
            guildId,
            userId: member.id,
            nickname: nickname.trim() === '' ? null : nickname.trim(),
            reason: reason || undefined,
          }),
        });
      }
      if (action === 'permaban') {
        return api('/api/actions/permaban', {
          method: 'POST',
          body: JSON.stringify({ guildId, userId: member.id, reason: reason || undefined }),
        });
      }
      if (action === 'ban') {
        return api('/api/actions/ban', {
          method: 'POST',
          body: JSON.stringify({ guildId, userId: member.id, reason: reason || undefined }),
        });
      }
      if (action === 'kick') {
        return api('/api/actions/kick', {
          method: 'POST',
          body: JSON.stringify({ guildId, userId: member.id, reason: reason || undefined }),
        });
      }
      if (action === 'timeout') {
        return api('/api/actions/timeout', {
          method: 'POST',
          body: JSON.stringify({
            guildId,
            userId: member.id,
            durationSec: duration,
            reason: reason || undefined,
          }),
        });
      }
      if (action === 'warn') {
        return api(`/api/guilds/${guildId}/warnings`, {
          method: 'POST',
          body: JSON.stringify({ userId: member.id, reason: reason || 'Kein Grund' }),
        });
      }
      if (action === 'role') {
        if (!roleId) throw new Error('Bitte eine Rolle wählen.');
        return api('/api/actions/assign-role', {
          method: 'POST',
          body: JSON.stringify({
            guildId,
            userId: member.id,
            roleId,
            op,
            reason: reason || undefined,
          }),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', guildId] });
      if (action === 'warn') qc.invalidateQueries({ queryKey: ['warnings', guildId] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const availableRoles = op === 'add'
    ? allRoles
        .filter((r) => !memberRoles.find((mr) => mr.id === r.id))
        .filter((r) => r.id !== guildId /* @everyone */)
        .sort((a, b) => b.position - a.position)
    : memberRoles;

  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {actionLabel(action)}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {action === 'role' && (
        <div className="mb-2 space-y-2">
          <div className="flex gap-1">
            {(['add', 'remove'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setOp(k);
                  setRoleId('');
                }}
                className={cn(
                  'rounded-md border px-3 py-1 text-xs',
                  op === k
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'bg-background hover:bg-accent'
                )}
              >
                {k === 'add' ? 'Hinzufügen' : 'Entfernen'}
              </button>
            ))}
          </div>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— Rolle wählen —</option>
            {availableRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {action === 'setnick' && (
        <div className="mb-2">
          <Label>Neuer Nickname (leer = zurücksetzen)</Label>
          <Input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={32}
            placeholder="Nickname"
          />
        </div>
      )}

      {action === 'timeout' && (
        <div className="mb-2">
          <Label>Dauer (Sekunden) — 0 = Timeout aufheben</Label>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value={60}>1 Minute</option>
            <option value={300}>5 Minuten</option>
            <option value={600}>10 Minuten</option>
            <option value={3600}>1 Stunde</option>
            <option value={21600}>6 Stunden</option>
            <option value={86400}>24 Stunden</option>
            <option value={604800}>7 Tage</option>
            <option value={2419200}>28 Tage (Max)</option>
            <option value={0}>Timeout aufheben</option>
          </select>
        </div>
      )}

      <div className="mb-2">
        <Label>Grund {action === 'warn' ? '' : '(optional)'}</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={action === 'warn' ? 'Grund der Verwarnung' : 'Grund'}
          className="min-h-[60px]"
          maxLength={500}
        />
      </div>

      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setError(null);
            const confirmLabels: Partial<Record<Exclude<ActionKind, null>, string>> = {
              ban: 'bannen',
              kick: 'kicken',
              permaban: 'permanent bannen (überlebt Unbans)',
            };
            const label = confirmLabels[action];
            if (label && !confirm(`${member.displayName} wirklich ${label}?`)) return;
            mut.mutate();
          }}
          disabled={mut.isPending || (action === 'role' && !roleId)}
        >
          {mut.isPending ? 'Läuft…' : actionLabel(action)}
        </Button>
      </div>
    </div>
  );
}

function actionLabel(a: Exclude<ActionKind, null>): string {
  switch (a) {
    case 'ban':
      return 'Bannen';
    case 'kick':
      return 'Kicken';
    case 'timeout':
      return 'Timeout';
    case 'warn':
      return 'Warnung ausstellen';
    case 'role':
      return 'Rolle ändern';
    case 'setnick':
      return 'Nickname setzen';
    case 'permaban':
      return 'Permaban';
  }
}
